<template>
	<div>
		<BaseHeader :title="$t('overview').toString()">
			<BaseButton @click="$router.push({ name: 'damageAssessorCaseCreate' })" class="btn-blue">
				Neuer Gutachterfall
			</BaseButton>
		</BaseHeader>

		<div class="main" v-if="!isLoading">
			<BaseCard :title="$t('assessor_cases').toString()">
				<template #afterTitle>
					<div class="flex items-end w-full">
						<BaseInput
							ref="searchField"
							v-model="searchValue"
							:label="$t('search_term')"
							class="mr-4 flex-grow"
							style="min-width: 500px;"
							@input="search"
							:filter-only="true"
							:placeholder="$t('search_term')"
						/>

						<BaseSelect
							ref="yearBaseSelect"
							v-model="selectedFilter"
							:multiple="false"
							:options="queryFilter"
							:filter-only="true"
							class="w-72"
							style="padding-right: 0px !important; padding-bottom: 0px !important;"
							:label="$t('filter')"
							@changed="filterChanged"
						/>
					</div>
				</template>

				<!-- Top pagination -->
				<div class="flex justify-between align-middle mb-4">
					<PaginationNumberOfPages :current-page="currentPage" :last-page="lastPage" :total="total" class="mr-4 self-center" />

					<div class="flex">
						<PaginationSizeSelect
							v-model="per_page"
							@input="pageSizeChanged"
							style="min-width: 140px; max-width: 140px"
							v-show="damageAssessorCases && damageAssessorCases.length"
						/>

						<BasePagination v-model="currentPage" :last-page="lastPage" :total="total" :is-blocked="isSearchRunning" />
					</div>
				</div>

				<!-- Table -->
				<div class="flex flex-col mb-10">
					<div class="w-full align-middle shadow overflow-x-auto border border-gray-200 rounded">
						<table v-if="damageAssessorCases.length > 0" class="w-full table-fixed">
							<thead class="w-full" style="height: fit-content;">
								<tr>
									<HeaderColumn>
										{{ $t('date') }}
									</HeaderColumn>

									<HeaderColumn>
										{{ $t('license_plate_opponent') }}
									</HeaderColumn>

									<HeaderColumn>
										{{ $t('customer') }}
									</HeaderColumn>

									<HeaderColumn v-if="selectedFilter && selectedFilter.value === 'all'">
										{{ $t('assigned_damage_assessor') }}
									</HeaderColumn>

									<HeaderColumn>
										{{ $t('status') }}
									</HeaderColumn>
								</tr>
							</thead>

							<tbody class="bg-white">
								<tr
									v-for="(damageAssessorCase, index) in damageAssessorCases"
									:key="damageAssessorCase.id"
									class="tr odd:bg-gray-200 w-full cursor-pointer"
									@click="redirectToDamageAssessorDetail(damageAssessorCase.id)"
								>
									<!-- Created at -->
									<td class="px-4 py-4" :class="getRowBorderClass(index)">
										<div>
											{{ getCaseCreatedAt(damageAssessorCase) ? getGermanDate(new Date(getCaseCreatedAt(damageAssessorCase))) : $t('no_data') }}
										</div>
									</td>

									<!-- Opponent license plate -->
									<td class="py-4" :class="getRowBorderClass(index)">
										{{ getCaseOpponentLicensePlate(damageAssessorCase) ?? $t('no_data') }}
									</td>

									<!-- Customer / Person -->
									<td class="py-4" :class="getRowBorderClass(index)">
										{{ getCasePersonName(damageAssessorCase) }}
									</td>

									<!-- Damage assessor -->
									<td v-if="selectedFilter && selectedFilter.value === 'all'" class="py-4" :class="getRowBorderClass(index)">
										{{ getDamageAssessorLabel(damageAssessorCase) }}
									</td>

									<!-- Status -->
									<td class="py-4" :class="getRowBorderClass(index)">
										{{ getBrokerCaseStatus(damageAssessorCase.status) }}
									</td>
								</tr>
							</tbody>
						</table>

						<div class="min-w-full p-4" v-else>
							{{ $t('no_entries_found') }}
						</div>
					</div>
				</div>

				<!-- Bottom pagination -->
				<div class="flex justify-between align-middle mt-4" v-show="damageAssessorCases && damageAssessorCases.length">
					<PaginationNumberOfPages :current-page="currentPage" :last-page="lastPage" :total="total" class="mr-8 self-center" />

					<div class="flex">
						<PaginationSizeSelect v-model="per_page" @input="pageSizeChanged" style="min-width: 140px; max-width: 140px" />

						<BasePagination v-model="currentPage" :last-page="lastPage" :total="total" :is-blocked="isSearchRunning" />
					</div>
				</div>
			</BaseCard>
		</div>

		<LoadingSpinner v-else />
	</div>
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader';
import BaseCard from '@/components/damagecases/forms/BaseCard';
import BaseButton from '@/components/forms/buttons/BaseButton.vue';
import BaseSelect from '@/components/forms/inputs/BaseSelect.vue';
import BaseInput from '@/components/forms/inputs/BaseInput.vue';
import HeaderColumn from '@/components/columns/HeaderColumn.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';

import Authentication from '@/mixins/Authentication';

import DamageAssessorCaseRepository from '@/api/repositories/DamageAssessorCaseRepository';

import { getBrokerCaseStatus } from '@/helpers/Utils';
import { getGermanDate } from '../../plugins/Utils';

import router from '@/router';
import PaginationNumberOfPages from '@/components/forms/buttons/PaginationNumberOfPages.vue';
import PaginationSizeSelect from '@/components/damagecases/forms/PaginationSizeSelect.vue';
import BasePagination from '@/components/forms/buttons/BasePagination.vue';

export default {
	name: 'DamageAssessorCaseOverview',

	components: {
		BaseInput,
		BaseHeader,
		BaseCard,
		BaseButton,
		BaseSelect,
		HeaderColumn,
		LoadingSpinner,

		PaginationNumberOfPages,
		PaginationSizeSelect,
		BasePagination,
	},

	mixins: [Authentication],

	data() {
		return {
			isLoading: false,

			isSearchRunning: false,

			searchValue: '',

			searchTimeoutId: null,

			currentPage: 1,

			lastPage: 1,

			total: 0,

			per_page: {
				display: '25',
				value: 25,
			},

			damageAssessorCases: [],

			brokerCases: [],

			brokerFilters: [
				{
					value: 'all',
					display: 'Alle',
				},
				{
					value: 'declined',
					display: 'Abgelehnte',
				},
			],

			selectedBrokerFilter: null,

			queryFilter: [
				{
					value: 'all',
					display: 'Alle',
				},
				{
					value: 'own_open',
					display: 'Eigene Offene',
				},
				{
					value: 'own_closed',
					display: 'Eigene Geschlossene',
				},
			],

			selectedFilter: null,
		};
	},

	mounted() {
		this.isLoading = true;

		this.selectedFilter = this.queryFilter[1];

		this.getAllCases();
	},

	beforeDestroy() {
		clearTimeout(this.searchTimeoutId);
	},

	methods: {
		getGermanDate,
		getBrokerCaseStatus,

		search() {
			clearTimeout(this.searchTimeoutId);

			this.searchTimeoutId = setTimeout(() => {
				if (this.currentPage !== 1) {
					this.currentPage = 1;

					return;
				}

				this.getAllCases();
			}, 400);
		},

		filterChanged() {
			if (this.currentPage !== 1) {
				this.currentPage = 1;

				return;
			}

			this.getAllCases();
		},

		pageSizeChanged() {
			if (this.currentPage !== 1) {
				this.currentPage = 1;

				return;
			}

			this.getAllCases();
		},

		getRowBorderClass(index) {
			return this.damageAssessorCases.length !== index + 1 ? 'border-b border-gray-200' : '';
		},

		getCaseCreatedAt(damageAssessorCase) {
			if (damageAssessorCase.broker_case?.assessor_case?.created_at) {
				return damageAssessorCase.broker_case.assessor_case.created_at;
			}

			return damageAssessorCase.created_at ?? null;
		},

		getCaseOpponentLicensePlate(damageAssessorCase) {
			if (damageAssessorCase.broker_case) {
				return damageAssessorCase.broker_case.causer_license_plate ?? null;
			}

			return damageAssessorCase.causer_license_plate ?? null;
		},

		getCasePerson(damageAssessorCase) {
			if (damageAssessorCase.broker_case) {
				return {
					firstName: damageAssessorCase.broker_case.first_name,

					lastName: damageAssessorCase.broker_case.last_name,
				};
			}

			return damageAssessorCase.person ?? null;
		},

		getCasePersonName(damageAssessorCase) {
			const person = this.getCasePerson(damageAssessorCase);

			if (!person) {
				return this.$t('no_data');
			}

			const name = [person.firstName, person.lastName].filter(value => value).join(' ');

			return name || this.$t('no_data');
		},

		getDamageAssessorLabel(damageAssessorCase) {
			const damageAssessor = damageAssessorCase.damage_assessor;

			if (!damageAssessor) {
				return this.$t('no_data');
			}

			const name = [damageAssessor.first_name, damageAssessor.last_name].filter(value => value).join(' ');

			return name || this.$t('no_data');
		},

		getBrokerCases() {
			if (this.selectedBrokerFilter === this.brokerFilters[0]) {
				new DamageAssessorCaseRepository()
					.getBrokerCases()
					.then(response => {
						this.brokerCases = response.data;
					})
					.catch(error => {
						console.log(error);
					});
			} else {
				new DamageAssessorCaseRepository()
					.getDeclinedCases()
					.then(response => {
						this.brokerCases = response.data;
					})
					.catch(error => {
						console.log(error);
					});
			}
		},

		getAllCases() {
			if (!this.selectedFilter) {
				return;
			}

			this.isSearchRunning = true;

			new DamageAssessorCaseRepository()
				.getAllCases(this.selectedFilter.value, this.per_page.value, this.currentPage, this.searchValue)
				.then(response => {
					const pagination = response.data;

					this.damageAssessorCases = pagination.data ?? [];

					this.currentPage = pagination.current_page ?? 1;

					this.lastPage = pagination.last_page ?? 1;

					this.total = pagination.total ?? 0;
				})
				.catch(error => {
					console.log(error);

					this.damageAssessorCases = [];
				})
				.finally(() => {
					this.isSearchRunning = false;

					this.isLoading = false;
				});
		},

		redirectToBrokerCaseDetail(id) {
			router.push({
				name: 'brokerCaseDetail',

				params: {
					id: id,
				},
			});
		},

		redirectToDamageAssessorDetail(id) {
			router.push({
				name: 'damageAssessorCaseDetail',

				params: {
					id: id,
				},
			});
		},
	},

	watch: {
		currentPage(newPage, oldPage) {
			if (newPage === oldPage) {
				return;
			}

			this.getAllCases();
		},
	},
};
</script>

<style scoped></style>
